<template>
  <Teleport to="body">
    <Transition name="fade">
      <div v-if="isOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6" @click.self="handleClose">
        <div class="flex max-h-full w-full max-w-2xl flex-col rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
          <div class="flex items-start justify-between gap-4 border-b border-slate-200 bg-slate-50 px-6 py-4">
            <div>
              <h2 class="text-lg font-semibold text-slate-900">{{ t('requestFiles.modal.title') }}</h2>
              <p class="mt-1 text-sm text-slate-500">{{ t('requestFiles.modal.description') }}</p>
            </div>
            <button type="button" class="rounded-full p-2 text-slate-500 hover:bg-slate-200 hover:text-slate-700" @click="handleClose">
              <span class="sr-only">{{ t('common.close') }}</span>
              ×
            </button>
          </div>

          <form class="space-y-6 overflow-y-auto px-6 py-6" @submit.prevent="handleSubmit">
            <div class="pb-2">
              <label class="mb-3 block text-sm font-medium text-slate-700">{{ t('requestFiles.modal.files') }}</label>
              <input
                ref="fileInput"
                type="file"
                multiple
                class="block w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 file:mr-4 file:rounded-lg file:border-0 file:bg-sky-500 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-sky-600"
                :accept="acceptedTypes"
                :disabled="isUploading"
                @change="handleFileChange"
              />
              <p class="mt-2 text-xs text-slate-500">{{ t('requestFiles.modal.allowedFormats') }}</p>
            </div>

            <!-- Tipo documental común: se aplica a todos y después se puede cambiar por archivo (ADSREC-0025). -->
            <div class="pb-2">
              <label class="mb-3 block text-sm font-medium text-slate-700">{{ t('requestFiles.modal.commonDocumentalType') }}</label>
              <select
                v-model="commonDocumentalType"
                class="block w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                :disabled="isUploading"
                @change="applyCommonDocumentalType"
              >
                <option value="" disabled>{{ t('requestFiles.modal.selectDocumentalType') }}</option>
                <option v-for="option in documentTypeOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
              <p class="mt-2 text-xs text-slate-500">{{ t('requestFiles.modal.commonDocumentalTypeHint') }}</p>
            </div>

            <ul v-if="entries.length" class="divide-y divide-slate-200 rounded-xl border border-slate-200" data-testid="upload-entries">
              <li v-for="entry in entries" :key="entry.key" class="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center">
                <div class="min-w-0 flex-1">
                  <p class="truncate text-sm font-medium text-slate-800" :title="entry.file.name">{{ entry.file.name }}</p>
                  <p v-if="entry.status === 'done'" class="text-xs text-emerald-600">{{ t('requestFiles.modal.uploaded') }}</p>
                  <p v-else-if="entry.status === 'uploading'" class="text-xs text-sky-600">{{ t('requestFiles.modal.uploading') }}</p>
                  <p v-else-if="entry.error" class="text-xs text-red-600">{{ entry.error }}</p>
                </div>
                <select
                  v-if="!entry.invalid && entry.status !== 'done'"
                  v-model="entry.documentalType"
                  class="w-full rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm text-slate-700 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200 sm:w-56"
                  :aria-label="t('requestFiles.modal.documentalTypeFor', { name: entry.file.name })"
                  :disabled="isUploading"
                >
                  <option value="" disabled>{{ t('requestFiles.modal.selectDocumentalType') }}</option>
                  <option v-for="option in documentTypeOptions" :key="option.value" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
                <button
                  v-if="entry.status !== 'done' && entry.status !== 'uploading'"
                  type="button"
                  class="self-end rounded-full px-2 text-lg leading-none text-slate-400 hover:text-slate-700 sm:self-center disabled:opacity-50"
                  :title="t('requestFiles.modal.remove')"
                  :aria-label="t('requestFiles.modal.remove')"
                  :disabled="isUploading"
                  @click="removeEntry(entry)"
                >
                  ×
                </button>
              </li>
            </ul>

            <div v-if="errorMessage" class="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {{ errorMessage }}
            </div>

            <div class="border-t border-slate-200 pt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                class="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="isUploading"
                @click="handleClose"
              >
                {{ hasUploaded ? t('common.close') : t('common.cancel') }}
              </button>
              <button
                type="submit"
                class="rounded-xl bg-[#2AB4D9] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#25A8CC] disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="isUploading || !pendingEntries.length"
              >
                {{ isUploading ? t('requestFiles.modal.uploading') : uploadButtonLabel }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import axios from 'axios';
import { toast } from 'vue3-toastify';
import { useI18n } from 'vue-i18n';
import { getRequestDocumentTypeOptions } from '@/constants/requestDocumentTypes';

const { t } = useI18n();

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  requestId: {
    type: [String, Number],
    required: true,
  },
});

const emit = defineEmits(['close', 'upload-success']);

const allowedExtensions = ['jpg', 'jpeg', 'png', 'pdf', 'doc', 'docx'];
const acceptedTypes = allowedExtensions.map((extension) => `.${extension}`).join(',');
const documentTypeOptions = computed(() => getRequestDocumentTypeOptions(t));

const maxFileSizeBytes = 5 * 1024 * 1024;

// Cada archivo elegido, con su tipo y su estado: pending, uploading, done o error.
// Los inválidos (formato o tamaño) quedan en la lista con el error y no se suben.
const entries = ref([]);
const commonDocumentalType = ref('');
const isUploading = ref(false);
const errorMessage = ref('');
const fileInput = ref(null);
let nextKey = 0;

const pendingEntries = computed(() => entries.value.filter((entry) => !entry.invalid && entry.status !== 'done'));
const hasUploaded = computed(() => entries.value.some((entry) => entry.status === 'done'));
const uploadButtonLabel = computed(() => (
  pendingEntries.value.length > 1
    ? t('requestFiles.modal.uploadCount', { count: pendingEntries.value.length })
    : t('requestFiles.modal.uploadOne')
));

const resetForm = () => {
  entries.value = [];
  commonDocumentalType.value = '';
  errorMessage.value = '';
  isUploading.value = false;
  if (fileInput.value) {
    fileInput.value.value = '';
  }
};

watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      resetForm();
    }
  }
);

const handleClose = () => {
  if (isUploading.value) {
    return;
  }

  resetForm();
  emit('close');
};

const validationError = (file) => {
  const extension = file.name.includes('.') ? file.name.split('.').pop().toLowerCase() : '';
  if (!allowedExtensions.includes(extension)) {
    return t('requestFiles.errors.formatNamed', { name: file.name });
  }
  if (file.size > maxFileSizeBytes) {
    return t('requestFiles.errors.sizeNamed', { name: file.name });
  }

  return '';
};

// Se pueden elegir archivos en varias tandas: se suman a la lista.
const handleFileChange = (event) => {
  const files = Array.from(event.target.files ?? []);
  errorMessage.value = '';

  files.forEach((file) => {
    const error = validationError(file);
    entries.value.push({
      key: nextKey++,
      file,
      documentalType: commonDocumentalType.value,
      status: error ? 'error' : 'pending',
      invalid: Boolean(error),
      error,
    });
  });

  event.target.value = '';
};

const applyCommonDocumentalType = () => {
  pendingEntries.value.forEach((entry) => {
    entry.documentalType = commonDocumentalType.value;
  });
};

const removeEntry = (entry) => {
  entries.value = entries.value.filter((item) => item.key !== entry.key);
};

const uploadEntry = async (entry) => {
  entry.status = 'uploading';
  entry.error = '';

  try {
    const formData = new FormData();
    formData.append('file', entry.file);
    formData.append('documental_type', entry.documentalType);

    const response = await axios.post(
      `${import.meta.env.VITE_API_URL}/request/update/upload/${props.requestId}`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    if (response.data?.value) {
      entry.status = 'done';
      return response.data.value;
    }

    entry.status = 'error';
    entry.error = t('requestFiles.errors.uploadNamed', { name: entry.file.name });
  } catch (error) {
    console.error('Error uploading document file:', error);
    const responseMessage = error?.response?.data?.message || error?.response?.data?.value;
    entry.status = 'error';
    entry.error = typeof responseMessage === 'string' && responseMessage
      ? `${entry.file.name}: ${responseMessage}`
      : t('requestFiles.errors.uploadNamed', { name: entry.file.name });
  }

  return null;
};

// Se suben de a uno, en orden: cada archivo queda con su tipo, su usuario y su
// fecha, y si uno falla, los demás se suben igual (ADSREC-0025).
const handleSubmit = async () => {
  if (isUploading.value) {
    return;
  }

  const toUpload = pendingEntries.value;
  if (!toUpload.length) {
    errorMessage.value = t('requestFiles.errors.fileRequired');
    return;
  }

  if (toUpload.some((entry) => !entry.documentalType)) {
    errorMessage.value = t('requestFiles.errors.documentalTypeRequired');
    return;
  }

  isUploading.value = true;
  errorMessage.value = '';

  const uploaded = [];
  for (const entry of toUpload) {
    const file = await uploadEntry(entry);
    if (file) {
      uploaded.push(file);
    }
  }

  isUploading.value = false;

  if (uploaded.length) {
    toast.success(uploaded.length > 1
      ? t('requestFiles.successCount', { count: uploaded.length })
      : t('requestFiles.success'));
    emit('upload-success', uploaded);
  }

  const failed = entries.value.filter((entry) => entry.status === 'error');
  if (failed.length) {
    errorMessage.value = t('requestFiles.errors.someFailed', { count: failed.length });
    toast.error(errorMessage.value);
    return;
  }

  // Close modal after successful upload (with small delay for UX)
  setTimeout(() => {
    handleClose();
  }, 300);
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
